import { useEffect, useState, useCallback, useMemo } from 'react';
import type { JSX } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Table, Tag, Button, Space, Typography, Input, Select, Popover, message, Modal } from 'antd';
import { ArrowLeftOutlined, MoreOutlined, SearchOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  queryForeignSyncTasks,
  queryForeignSyncJobDetail,
  queryForeignSyncTaskLog,
  type ForeignSyncTaskItem,
  type ForeignSyncJobDetail,
  type ForeignSyncTaskInfo,
  type TaskLogResponse,
} from '@/apis/simulation';

const { Text } = Typography;

// 显式复制按钮 (antd Text copyable 图标在某些场景不渲染, 用 Button+CopyOutlined 兜底)
function CopyableText({ value, ellipsis, maxWidth }: { value: string; ellipsis?: boolean; maxWidth?: number }): JSX.Element {
  return (
    <Space size={2}>
      <Text ellipsis={ellipsis} style={maxWidth ? { maxWidth } : undefined}>{value}</Text>
      <Button
        type="text"
        size="small"
        icon={<CopyOutlined />}
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => message.success('已复制')).catch(() => {});
        }}
      />
    </Space>
  );
}

const STATUS_META: Record<string, { color: string; label: string }> = {
  SUBMITTED: { color: 'processing', label: '同步中' },
  COMPLETED: { color: 'success', label: '同步完成' },
  SYNC_FAILED: { color: 'error', label: '同步失败' },
};

const SCENE_STATUS_META: Record<number, { color: string; label: string }> = {
  1: { color: 'default', label: '已录入' },
  5: { color: 'processing', label: '质检中' },
  7: { color: 'success', label: '瑕疵合格' },
  8: { color: 'success', label: '质检合格' },
  9: { color: 'error', label: '质检失败' },
};

export default function ForeignSyncTasksPage(): JSX.Element {
  const { dispatchJobId = '' } = useParams<{ dispatchJobId: string }>();
  const [listLoading, setListLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [jobDetailError, setJobDetailError] = useState(false);
  const [detail, setDetail] = useState<ForeignSyncJobDetail | undefined>();
  const [scenes, setScenes] = useState<ForeignSyncTaskItem[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logData, setLogData] = useState<TaskLogResponse | null>(null);
  const [logTaskId, setLogTaskId] = useState<string>('');
  const [apiCodeFilter, setApiCodeFilter] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [sceneNameFilter, setSceneNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [apiCodeInput, setApiCodeInput] = useState('');
  const [taskIdInput, setTaskIdInput] = useState('');
  const [sceneNameInput, setSceneNameInput] = useState('');

  useEffect(() => {
    if (!dispatchJobId) return;
    // list (本地 SQL, 快) — progress + scene 数据, 立即渲染顶部 Tag + 表格行
    setListLoading(true);
    queryForeignSyncTasks({ pageSize: 1000, dispatchJobId })
      .then((r) => setScenes(r?.items ?? []))
      .catch(() => setScenes([] as ForeignSyncTaskItem[]))
      .finally(() => setListLoading(false));
    // job-detail (调海外 CF, ~2s) — 只拿 task 列表, 独立 loading; 失败不弹全局错, 表格提示
    setTaskLoading(true);
    setJobDetailError(false);
    queryForeignSyncJobDetail(dispatchJobId)
      .then((d) => { setDetail(d); setJobDetailError(false); })
      .catch(() => { setDetail(undefined); setJobDetailError(true); })
      .finally(() => setTaskLoading(false));
  }, [dispatchJobId]);

  const openTaskLog = useCallback(async (taskId: string) => {
    setLogTaskId(taskId);
    setLogData(null);
    setLogLoading(true);
    try {
      const r = await queryForeignSyncTaskLog(taskId);
      setLogData(r ?? null);
    } catch (e) {
      message.error(`查 task 日志失败: ${(e as Error).message}`);
    } finally {
      setLogLoading(false);
    }
  }, []);

  // CF task 按 apiCode 索引, CF 不通时为空, 表格用 scene 行渲染 task_id/时间列显示 '—'
  const taskMap = useMemo(() => {
    const m = new Map<string, ForeignSyncTaskInfo>();
    for (const t of detail?.tasks?.tasks ?? []) {
      const ac = (t.task_name || '').replace(/^case-/, '').toLowerCase();
      if (ac) m.set(ac, t);
    }
    return m;
  }, [detail]);

  // progress 从 list 的 scene 聚合 (sim 侧 sync status, 等价物化表), 不等 job-detail 立即渲染
  const progress = useMemo(() => {
    const total = scenes.length;
    const completed = scenes.filter((s) => s.status === 'COMPLETED').length;
    const failed = scenes.filter((s) => s.status === 'SYNC_FAILED').length;
    const submitted = scenes.filter((s) => s.status === 'SUBMITTED').length;
    const syncStatus = submitted > 0 ? 'SUBMITTED' : (failed > 0 ? 'SYNC_FAILED' : (total > 0 ? 'COMPLETED' : undefined));
    return { total, completed, failed, submitted, syncStatus };
  }, [scenes]);

  // 以 scene 行为主 (list 本地 SQL, CF 不通也能渲染), CF task 数据按 apiCode 合并补充
  const filteredTasks = useMemo(() => {
    const apiQ = apiCodeFilter.trim().toLowerCase();
    const taskQ = taskIdFilter.trim().toLowerCase();
    const nameQ = sceneNameFilter.trim().toLowerCase();
    const rows = scenes.map((s) => {
      const ac = (s.apiCode || '').toLowerCase();
      const t = taskMap.get(ac);
      return {
        task_id: t?.task_id,
        task_name: s.apiCode ? `case-${s.apiCode}` : undefined,
        apiCode: s.apiCode,
        sceneId: s.sceneId ?? undefined,
        sceneName: s.sceneName ?? undefined,
        syncDest: s.syncDest || undefined,
        fsStatus: s.status || undefined,
        sceneStatus: s.sceneStatus ?? undefined,
        start_time: t?.start_time,
        end_time: t?.end_time,
        duration: t?.duration,
        error_message: t?.error_message,
      };
    });
    return rows.filter((r) => {
      if (apiQ && !(r.apiCode || '').toLowerCase().includes(apiQ)) return false;
      if (taskQ && !(r.task_id || '').toLowerCase().includes(taskQ)) return false;
      if (nameQ && !(r.sceneName || '').toLowerCase().includes(nameQ)) return false;
      if (statusFilter && r.fsStatus !== statusFilter) return false;
      return true;
    });
  }, [scenes, taskMap, apiCodeFilter, taskIdFilter, sceneNameFilter, statusFilter]);

  const handleSearch = useCallback(() => {
    setApiCodeFilter(apiCodeInput);
    setTaskIdFilter(taskIdInput);
    setSceneNameFilter(sceneNameInput);
  }, [apiCodeInput, taskIdInput, sceneNameInput]);

  const handleReset = useCallback(() => {
    setApiCodeInput(''); setTaskIdInput(''); setSceneNameInput('');
    setApiCodeFilter(''); setTaskIdFilter(''); setSceneNameFilter('');
    setStatusFilter(undefined);
  }, []);

  // 合并行: scene (list, 本地 SQL) + CF task (按 apiCode 合并, CF 不通时 task_* 为 undefined)
  type MergedRow = {
    task_id?: string; task_name?: string; apiCode?: string | null;
    sceneId?: number; sceneName?: string; syncDest?: string;
    fsStatus?: string; sceneStatus?: number;
    start_time?: string; end_time?: string; duration?: number; error_message?: string;
  };

  const taskColumns = useMemo<ColumnsType<MergedRow>>(() => [
    {
      title: 'task_id',
      dataIndex: 'task_id',
      width: 180,
      ellipsis: true,
      render: (v: string | null | undefined) => v ? <CopyableText value={v.slice(-16)} maxWidth={140} /> : '—',
    },
    {
      title: 'apiCode',
      dataIndex: 'apiCode',
      width: 220,
      render: (v: string | null | undefined) => v ? <CopyableText value={v} maxWidth={180} /> : <Text type="secondary">—</Text>,
    },
    {
      title: '场景名称',
      dataIndex: 'sceneName',
      width: 200,
      ellipsis: true,
      render: (v: string | null | undefined) => v ? <Text>{v}</Text> : '—',
    },
    {
      title: '场景 ID',
      dataIndex: 'sceneId',
      width: 100,
      render: (v: number | null | undefined) => v != null ? v : '—',
    },
    {
      title: '目标 BOS',
      dataIndex: 'syncDest',
      width: 280,
      ellipsis: true,
      render: (v: string | null | undefined) => v ? <CopyableText value={v} ellipsis maxWidth={240} /> : '—',
    },
    {
      title: '同步状态',
      dataIndex: 'fsStatus',
      width: 100,
      render: (v: string | null | undefined) => {
        if (!v) return '—';
        const m = STATUS_META[v];
        return <Tag color={m?.color || 'default'}>{m?.label || v}</Tag>;
      },
    },
    {
      title: '场景状态',
      dataIndex: 'sceneStatus',
      width: 100,
      render: (v: number | null | undefined) => {
        if (v == null) return '—';
        const m = SCENE_STATUS_META[v];
        return <Tag color={m?.color || 'default'}>{m?.label || v}</Tag>;
      },
    },
    { title: '开始', dataIndex: 'start_time', width: 160, render: (v: string | null | undefined) => v ? dayjs(v).format('YYYY/M/D HH:mm:ss') : '—' },
    { title: '结束', dataIndex: 'end_time', width: 160, render: (v: string | null | undefined) => v ? dayjs(v).format('YYYY/M/D HH:mm:ss') : '—' },
    { title: '耗时(s)', dataIndex: 'duration', width: 90, render: (v: number | null | undefined) => (v != null ? v : '—') },
    { title: '错误', dataIndex: 'error_message', ellipsis: true, render: (v: string | null | undefined) => v || '—' },
    {
      title: '操作',
      key: 'action',
      width: 88,
      align: 'center',
      fixed: 'right',
      render: (_, r) => (
        <Popover
          trigger="click"
          content={(
            <div style={{ minWidth: 160 }}>
              <div style={{ marginBottom: 8 }}>
                <a
                  style={{ color: '#5e6ad2', cursor: r.task_id ? 'pointer' : 'not-allowed', opacity: r.task_id ? 1 : 0.5 }}
                  onClick={() => r.task_id && openTaskLog(r.task_id)}
                >
                  Task日志
                </a>
              </div>
              {r.sceneId ? (
                <div>
                  <Link to={`/scene/${r.sceneId}`} style={{ color: '#5e6ad2' }}>场景详情</Link>
                </div>
              ) : null}
            </div>
          )}
        >
          <Button size="small" icon={<MoreOutlined />} />
        </Popover>
      ),
    },
  ], [openTaskLog]);

  return (
    <Card
      title={
        <Space>
          <Link to="/scene-generation"><ArrowLeftOutlined /> 返回</Link>
          <span>海外同步 task 列表</span>
          <Text type="secondary">{dispatchJobId}</Text>
        </Space>
      }
      loading={listLoading}
    >
      {progress.total > 0 ? (
        <Space style={{ marginBottom: 16 }} size={8} wrap>
          <Tag>总任务数 {progress.total}</Tag>
          <Tag color="success">成功 {progress.completed}</Tag>
          <Tag color="error">失败 {progress.failed}</Tag>
          <Text type="secondary">job 状态: {progress.syncStatus || '—'}</Text>
        </Space>
      ) : null}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="apiCode"
          value={apiCodeInput}
          onChange={(e) => setApiCodeInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ width: 180 }}
        />
        <Input
          placeholder="task_id"
          value={taskIdInput}
          onChange={(e) => setTaskIdInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ width: 180 }}
        />
        <Input
          placeholder="场景名称"
          value={sceneNameInput}
          onChange={(e) => setSceneNameInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ width: 180 }}
        />
        <Select
          placeholder="同步状态"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          allowClear
          style={{ width: 140 }}
          options={[
            { label: '同步中', value: 'SUBMITTED' },
            { label: '同步完成', value: 'COMPLETED' },
            { label: '同步失败', value: 'SYNC_FAILED' },
          ]}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          查询
        </Button>
        <Button onClick={handleReset}>
          重置
        </Button>
      </Space>
      <Table
        rowKey={(r) => r.task_id || r.task_name || ''}
        columns={taskColumns}
        dataSource={filteredTasks}
        loading={taskLoading}
        locale={{ emptyText: jobDetailError ? '海外 CF 暂不可达，请稍后重试' : '无数据' }}
        pagination={{
          pageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: ['50', '100', '200'],
          showTotal: (t) => `共 ${t} 条`,
        }}
        scroll={{ x: 1200 }}
      />
      <Modal
        title={`Task 日志 - ${logTaskId.slice(-16)}`}
        open={!!logTaskId}
        onCancel={() => setLogTaskId('')}
        footer={null}
        width={800}
      >
        {logLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>加载中...</div>
        ) : logData ? (
          <div>
            {logData.logDownloadUrl ? (
              <p><a href={logData.logDownloadUrl} target="_blank" rel="noreferrer">下载完整日志</a></p>
            ) : null}
            {logData.logS3Path ? (
              <p style={{ color: '#999', fontSize: 12 }}>{logData.logS3Path}</p>
            ) : null}
            <pre style={{ maxHeight: 400, overflow: 'auto', background: '#fafafa', padding: 8, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {logData.logContent || '无日志内容'}
            </pre>
          </div>
        ) : (
          <div>无日志</div>
        )}
      </Modal>
    </Card>
  );
}
